// Pure geometry and table helpers behind the trend chart (hand-built SVG, no chart library).
// Keeping them out of the component makes the scale, gap handling and pointer math unit-testable.

import { DASH, fmtDayShort, fmtDayWeekday, fmtInt, fmtMoney, fmtMoneyCompact, fmtNumber1, fmtPct, fmtPct0 } from "./statsFormat";

/** Metrics the trend chart can plot: tooltip/table format and compact axis format. */
export const TREND_METRICS = {
  occ: { label: "Ocupación", format: fmtPct, axis: fmtPct0 },
  rev: { label: "Ingresos cobrados", format: fmtMoney, axis: fmtMoneyCompact },
  res: { label: "Reservas", format: fmtNumber1, axis: fmtInt },
};

/** Values of a series without the gaps, for a sparkline that has no notion of "closed". */
export const sparkValues = (series) => series.map((p) => p.value).filter((v) => typeof v === "number");

/** Rounds a raw step up to 1, 2, 2.5, 5 or 10 times a power of ten. */
export const niceStep = (raw) => {
  const power = 10 ** Math.floor(Math.log10(raw));
  const f = raw / power;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * power;
};

// A series that is all zero still needs a readable axis.
const EMPTY_MAX = { occ: 1, rev: 1000, res: 4 };

/**
 * Y axis for a metric: about four divisions with "nice" steps, starting at zero (bars/areas must
 * not hide a baseline). Occupancy is capped at 100 % with steps of at most 25 points.
 * @returns {{top: number, step: number, ticks: number[]}}
 */
export const buildAxis = (metric, maxValue) => {
  const max = maxValue > 0 ? maxValue : EMPTY_MAX[metric];
  let step = niceStep(max / 4);
  if (metric === "occ") step = Math.max(Math.min(step, 0.25), 0.05);
  let top = Math.ceil(max / step) * step;
  if (metric === "occ") top = Math.min(1, top);
  const count = Math.round(top / step);
  // toPrecision removes float noise such as 0.15000000000000002
  const ticks = Array.from({ length: count + 1 }, (_, i) => Number((i * step).toPrecision(12)));
  return { top: ticks[ticks.length - 1], step, ticks };
};

/** Indexes of the x labels: `count` evenly spread points from first to last, without repeats. */
export const xTickIndexes = (length, count) => {
  if (length <= 0) return [];
  const ticks = Math.min(length, count);
  if (ticks === 1) return [0];
  const seen = new Set();
  for (let k = 0; k < ticks; k += 1) seen.add(Math.round((k * (length - 1)) / (ticks - 1)));
  return [...seen];
};

/** Splits a series into runs of consecutive known values; a null (closed day) ends a run. */
export const contiguousRuns = (points) => {
  const runs = [];
  let run = [];
  points.forEach((point, index) => {
    if (typeof point.value === "number") {
      run.push({ ...point, index });
    } else if (run.length) {
      runs.push(run);
      run = [];
    }
  });
  if (run.length) runs.push(run);
  return runs;
};

const f1 = (n) => n.toFixed(1);
const runPath = (run, x, y) => run.map((p, i) => `${i ? "L" : "M"}${f1(x(p.index))},${f1(y(p.value))}`).join("");

/** SVG path of the line, one subpath per run so the line breaks over a gap instead of crossing it. */
export const linePath = (points, x, y) => contiguousRuns(points).map((run) => runPath(run, x, y)).join("");

/** SVG path of the area under each run, closed down to the baseline `y0`. */
export const areaPath = (points, x, y, y0) =>
  contiguousRuns(points)
    .map((run) => `${runPath(run, x, y)}L${f1(x(run[run.length - 1].index))},${f1(y0)}L${f1(x(run[0].index))},${f1(y0)}Z`)
    .join("");

/** Index of the point closest to a pointer x position, clamped to the series. */
export const nearestIndex = (pointerX, plotLeft, plotWidth, length) => {
  if (length <= 1) return 0;
  const raw = Math.round(((pointerX - plotLeft) * (length - 1)) / plotWidth);
  return Math.max(0, Math.min(length - 1, raw));
};

/** "Lun, 5 oct" for a daily point, "Semana del 5 oct" for a weekly bucket. */
export const bucketLabel = (date, bucket) => (bucket === 1 ? fmtDayWeekday(date) : `Semana del ${fmtDayShort(date)}`);

/**
 * Text twin of the chart ("Ver tabla"): the same numbers for people who cannot read a line chart.
 * Rows pair points by position; a missing point (shorter comparison, closed day) is a dash.
 * `metrics` is the screen's metric table (court default; Beauty names its third metric "Turnos").
 */
export const buildTrendTable = ({ current, comparison, bucket, metric, comparisonName, metrics = TREND_METRICS }) => {
  const format = metrics[metric].format;
  const unit = bucket === 1 ? "Fecha" : "Semana";
  const headers = [unit, "Este período"];
  if (comparison) headers.push(`${unit} comparada`, comparisonName);
  const value = (p) => (p ? format(p.value) : DASH);
  const rows = current.map((point, i) => {
    const row = [bucketLabel(point.date, bucket), value(point)];
    if (comparison) {
      const other = comparison[i];
      row.push(other ? bucketLabel(other.date, bucket) : DASH, value(other));
    }
    return row;
  });
  return { headers, rows };
};

/* ------------------------------------- heatmap ------------------------------------- */

/**
 * Sequential ramp for the heatmap: one hue (the brand lime), light to dark, so "darker = busier"
 * reads without a legend. Interpolated linearly between these stops.
 */
export const HEAT_RAMP = ["#e6ecc8", "#d5dea9", "#c1cc82", "#adbb58", "#97a608", "#839004", "#6f7a03", "#5b6403", "#485002"];
const RAMP_RGB = HEAT_RAMP.map((hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)));

/** Color for an occupancy between 0 and 1 (clamped), as `rgb(r,g,b)`. */
export const rampColor = (value) => {
  const position = Math.max(0, Math.min(1, value)) * (HEAT_RAMP.length - 1);
  const i = Math.min(HEAT_RAMP.length - 2, Math.floor(position));
  const f = position - i;
  const [r, g, b] = RAMP_RGB[i].map((channel, k) => Math.round(channel + (RAMP_RGB[i + 1][k] - channel) * f));
  return `rgb(${r},${g},${b})`;
};

/** Text twin of the heatmap ("Ver tabla"): a row per weekday, a column per hour, dash when closed. */
export const buildHeatTable = (grid) => ({
  headers: ["Día", ...grid.hours.map((hour) => `${String(hour).padStart(2, "0")} h`)],
  rows: grid.rows.map((row) => [row.label, ...row.cells.map((cell) => fmtPct0(cell.value))]),
});
